import React from "react";
import { observer } from "mobx-react";
import {
  generatePath,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";

import WorkoutStore from "stores/WorkoutStore";
import WorkoutDetailsStore from "stores/WorkoutDetailsStore";

import { NavListItem } from "components/Layout";
import { NavLinkItemType } from "components/Layout/NavListItem/NavListItem";

import { capitalize } from "utils/common";
import ROUTES from "navigation/routes";

import { CONTENT_STORE_TYPE_LABEL } from "constants/workout";

type ParamsType = {
  categoryId?: string;
  familyId?: string;
  workoutGroup?: string;
  workoutId?: string;
  movementId?: string;
  workoutInWorkoutCategoryId?: string;
};

const NavList: React.FC = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<ParamsType>();

  const categoryId = params.categoryId
    ? Number.parseInt(params.categoryId)
    : undefined;
  const workoutId = params.workoutId
    ? Number.parseInt(params.workoutId)
    : undefined;
  const workoutGroup = params.workoutGroup;
  const movementId = params.movementId
    ? Number.parseInt(params.movementId)
    : undefined;
  const workoutInWorkoutCategoryId = params.workoutInWorkoutCategoryId
    ? Number.parseInt(params.workoutInWorkoutCategoryId)
    : undefined;
  const familyId = location.state?.familyId ? location.state?.familyId : params.familyId ? params.familyId : WorkoutStore.familyCategories.find(cat => cat.id === categoryId)?.familyId;
  const isFavorites = location.pathname.includes(ROUTES.FAVORITES);
  const isToday = location.pathname.includes(ROUTES.TODAY);

  const familyTitle = React.useMemo(
    () => 
      location.state?.familyTitle
      ? capitalize(location.state?.familyTitle)
      : capitalize(WorkoutStore.families.find(fam => fam.id === familyId)?.title),
    [location.state?.categoryTitle, familyId]
  );

  const categoryTitle = React.useMemo(
    () =>
      WorkoutStore.details?.title
        ? capitalize(WorkoutStore.details?.title)
        : location.state?.categoryTitle ? location.state?.categoryTitle : undefined,
    [WorkoutStore.details?.title, location.state?.categoryTitle]
  );

  const workoutTitle = React.useMemo(() => {
    const title =
      WorkoutDetailsStore.details?.label || WorkoutDetailsStore.details?.title || location.state?.workoutTitle;
    return title ? capitalize(title) : undefined;
  }, [WorkoutDetailsStore.details?.label, WorkoutDetailsStore.details?.title, location.state?.workoutTitle]);

  const movementTitle = React.useMemo(() => {
    return movementId
      ? capitalize(
          WorkoutDetailsStore.getMovementDetails(movementId)?.title || location.state?.movementTitle,
        )
      : undefined;
  }, [movementId]);

  const onClick = (route: string, routeParams?: any, location?: any) => () => {
    navigate(generatePath(route, routeParams), location);
  };

  const firstItem: NavLinkItemType = isToday
    ? { title: "Today", onClick: onClick(ROUTES.TODAY) }
    : isFavorites
    ? { title: CONTENT_STORE_TYPE_LABEL.STANDARD_FAVORITES, onClick: onClick(ROUTES.WORKOUT_FAVORITES) }
    : { title: "Workouts", onClick: onClick(ROUTES.WORKOUT) };

  const navListItems = React.useMemo(() => {
    const items: NavLinkItemType[] = [];

    items.push(firstItem);

    if (isToday && workoutId) {
      items.push({
        title: workoutTitle || "",
        onClick: onClick(ROUTES.TODAY_WORKOUT_DETAILS, {
          workoutId,
          workoutInWorkoutCategoryId,
        }),
      });

      if (movementId) {
        items.push({
          title: movementTitle || "",
          onClick: onClick(ROUTES.TODAY_WORKOUT_DETAILS_MOVEMENT_DETAILS, {
            workoutId,
            workoutInWorkoutCategoryId,
            movementId,
          }),
        });
      }

      return items;
    }

    if (isFavorites && workoutId) {
      items.push({
        title: workoutTitle || "",
        onClick: onClick(ROUTES.WORKOUT_FAVORITES_WORKOUT_DETAILS, {
          workoutId,
        }),
      });

      if (movementId) {
        items.push({
          title: movementTitle || "",
          onClick: onClick(
            ROUTES.WORKOUT_FAVORITES_WORKOUT_DETAILS_MOVEMENT_DETAILS,
            { workoutId, movementId },
          ),
        });
      }

      return items;
    }

    if (familyId) {
      items.push({
        title: familyTitle,
        onClick: () =>
          navigate(generatePath(ROUTES.WORKOUT_FAMILY_DETAILS, { familyId }), {
            state: { familyTitle, categoryId: categoryId, familyId: familyId },
          }),
      });
    }

    if (categoryId) {
      items.push({
        title: categoryTitle || location.state?.title || "",
        onClick: onClick(ROUTES.WORKOUT_CATEGORY_DETAILS, {
          categoryId,
        }, {state: { familyId: familyId, familyTitle: familyTitle, categoryId: categoryId, categoryTitle: categoryTitle },}),
      });

      if (workoutGroup && workoutGroup !== "all") {
        items.push({
          title: capitalize(workoutGroup),
          onClick: onClick(ROUTES.WORKOUT_CATEGORY_DETAILS_WORKOUTS, {
            categoryId,
            workoutGroup,
          }, {state: { categoryId: categoryId, familyId: familyId, familyTitle: familyTitle, groupTitle: capitalize(workoutGroup), categoryTitle: categoryTitle },}),
        });

        if (workoutId) {
          items.push({
            title: workoutTitle || "",
            onClick: onClick(
              ROUTES.WORKOUT_CATEGORY_DETAILS_WORKOUTS_WORKOUT_DETAILS,
              {
                categoryId,
                workoutGroup,
                workoutId,
              }, {state: { categoryId: categoryId, familyId: familyId, familyTitle: familyTitle, id: workoutId, workoutTitle: workoutTitle, groupTitle: capitalize(workoutGroup), categoryTitle: categoryTitle },}
            ),
          });

          if (movementId) {
            items.push({
              title: movementTitle || "",
              onClick: onClick(
                ROUTES.WORKOUT_CATEGORY_DETAILS_WORKOUTS_WORKOUT_DETAILS_MOVEMENT_DETAILS,
                {
                  categoryId,
                  workoutGroup,
                  workoutId,
                  movementId,
                }, {state: { categoryId: categoryId, familyId: familyId, familyTitle: familyTitle, workoutId: workoutId, movementId: movementId, movementTitle: movementTitle, workoutTitle: workoutTitle, groupTitle: capitalize(workoutGroup), categoryTitle: categoryTitle },}
              ),
            });
          }
        }
      } else if (workoutId) {
        items.push({
          title: workoutTitle || "",
          onClick: onClick(
            ROUTES.WORKOUT_CATEGORY_DETAILS_WORKOUT_DETAILS,
            {
              categoryId,
              workoutId,
            }, {state: { categoryId: categoryId, familyId: familyId, familyTitle: familyTitle, id: workoutId, workoutTitle: workoutTitle, categoryTitle: categoryTitle },}
          ),
        });

        if (movementId) {
          items.push({
            title: movementTitle || "",
            onClick: onClick(
              ROUTES.WORKOUT_CATEGORY_DETAILS_WORKOUT_DETAILS_MOVEMENT_DETAILS,
              {
                categoryId,
                workoutId,
                movementId,
              }, {state: { categoryId: categoryId, familyId: familyId, familyTitle: familyTitle, workoutId: workoutId, movementId: movementId, title: movementTitle, workoutTitle: workoutTitle, categoryTitle: categoryTitle },}
            ),
          });
        }
      }
    }

    return items;
  }, [
    isFavorites,
    isToday,
    workoutId,
    workoutInWorkoutCategoryId,
    workoutTitle,
    movementId,
    movementTitle,
    categoryId,
    categoryTitle,
    workoutGroup,
    location.state,
  ]);

  return (
    <>
      {navListItems.length > 1 && navListItems.map((item: any, index: number) => (
        <NavListItem
          key={`${index}-${item.title}`}
          title={item.title}
          onClick={item.onClick}
          isLastItem={index === navListItems.length - 1}
        />
      ))}
    </>
  );
});

export default NavList;
